import React, { useEffect, useState } from "react"
import { SiteOption } from "../../../types/SiteOption"
import { BrandOption } from "../../../types/BrandOption"
import { BrandsData } from "../../../hooks/useBrands"
import { ModuleVOConfigScope } from "../moduleVoNavigation"
import SiteSelect from "./SiteSelect"
import BrandSelect from "./BrandSelect"

interface Props {
    scope: ModuleVOConfigScope
    disabled: boolean
    sites: SiteOption[]
    allBrandsData: BrandsData
    siteBrands: BrandOption[]
    selectedSite: SiteOption | null
    onSiteChange: (site: SiteOption | null) => void
    selectedBrand: BrandOption | null
    onBrandChange: (brand: BrandOption | null) => void
    // En mode édition (disabled), la cible éditée ne comporte pas forcément la dimension
    // secondaire (ex : un site édité seul, sans marque) : dans ce cas on ne veut ni charger ni
    // afficher ce select. Sans effet en mode configuration (l'utilisateur choisit lui-même via le
    // bouton "Configuration avancée").
    hasSecondaryTarget: boolean
}

/**
 * Variante "select simple" de ScopeSelectors, pour les frais fixes : pas de multi-site ni de
 * multi-marque, juste un site seul, une marque seule, ou une combinaison des deux via le bouton
 * "Configuration avancée" — visible uniquement une fois la première entité choisie.
 */
export default function FixedFeeScopeSelectors({
    scope,
    disabled,
    sites,
    allBrandsData,
    siteBrands,
    selectedSite,
    onSiteChange,
    selectedBrand,
    onBrandChange,
    hasSecondaryTarget,
}: Props) {
    const [ advanced, setAdvanced ] = useState(false)

    const primarySelected = scope === 'brand' ? selectedBrand !== null : selectedSite !== null

    // La configuration avancée n'a de sens qu'une fois la première entité choisie : si elle est
    // retirée, on referme et on vide l'éventuelle sélection secondaire.
    useEffect(() => {
        if (disabled || primarySelected) {
            return
        }

        setAdvanced(false)

        if (scope === 'brand' && selectedSite !== null) {
            onSiteChange(null)
        }
        if (scope === 'site' && selectedBrand !== null) {
            onBrandChange(null)
        }
    }, [ primarySelected, disabled ])

    if (scope === 'global') {
        return null
    }

    const sitesForSelectedBrand = scope === 'brand' && selectedBrand
        ? sites.filter(site => allBrandsData.siteIdsByBrand.get(selectedBrand.value)?.has(site.value))
        : sites

    const showAdvancedToggle = !disabled && primarySelected && !advanced
    const showSecondary = disabled ? hasSecondaryTarget : (primarySelected && advanced)

    return (
        <div className="row">
            <div className="col-md-6">
                {scope === 'brand'
                    ? <BrandSelect brands={allBrandsData.brands} value={selectedBrand} onChange={onBrandChange} disabled={disabled} />
                    : <SiteSelect sites={sites} value={selectedSite} onChange={onSiteChange} disabled={disabled} label="Site" />
                }
            </div>
            <div className="col-md-6 tw-flex tw-flex-col">
                {showAdvancedToggle && (
                    <div className="tw-flex tw-flex-col tw-items-center">
                        <label className="mb-1 invisible"><small>&nbsp;</small></label>
                        <button type="button" className="btn btn-outline-secondary btn-sm mb-3" onClick={() => setAdvanced(true)}>
                            Configuration avancée
                        </button>
                    </div>
                )}
                {showSecondary && (
                    scope === 'brand'
                        ? <SiteSelect sites={sitesForSelectedBrand} value={selectedSite} onChange={onSiteChange} disabled={disabled} label="Site" />
                        : <BrandSelect brands={siteBrands} value={selectedBrand} onChange={onBrandChange} disabled={disabled} />
                )}
            </div>
        </div>
    )
}
